import { Button, Link, ListSubheader } from '@material-ui/core';
import Fab from '@material-ui/core/Fab';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Add from '@material-ui/icons/Add';
import { Alert } from '@material-ui/lab';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useAppUser } from 'components/AppUserProvider';
import { useAuthUser } from 'components/AuthUserProvider';
import { FullscreenLoading } from 'components/FullscreenLoading';
import { useLatestOffers } from 'components/LatestOffersProvider';
import { NoSubscriptionAlert, TrialAlert } from 'components/NoSubscriptionAlert';
import { useOfferDrafts } from 'components/OfferDraftsProvider';
import { useOrg, useOrgIdLocalStorage } from 'components/OrgProvider';
import { useOrgUserEmailInvites } from 'components/OrgUserEmailInvitesProvider';
import { useOrgUserInvites } from 'components/OrgUserInvitesProvider';
import { useOrgUser } from 'components/OrgUserProvider';
import { PageNavigation } from 'components/PageNavigation';
import { PriceFieldSugegstionsList } from 'components/PriceFieldSuggestionsList';
import { messaging, registerToken } from 'FirebaseConfigs';
import { useUrlParam } from 'hooks/useUrlParam';
import { Paths } from 'Paths';

import { filterOffers, OfferItem } from './Offer/Offers';
import { MyOrgUserInvitesList, useMyOrgUserEmailInvites, useMyOrgUserInvites } from './SelectOrgScreen';
import { OrgUserInviteList } from './Users/OrgUsersScreen';

const emptyArray: any[] = []

function getNotificationStatus() {
    try {
        return Notification.permission
    } catch (e) {
        console.error(e)
        return 'denied'
    }
}

export function Dashboard() {
    const [org, loadingOrg] = useOrg()
    const [authUser, loadingAuthUser] = useAuthUser()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [myOrgUserInvites, loadingMyOrgUserInvites] = useMyOrgUserInvites()
    const [myOrgUserEmailInvites, loadingMyOrgUserEmailInvites] = useMyOrgUserEmailInvites()
    const [orgUserInvites, loadingOrgUserInvites] = useOrgUserInvites()
    const [orgUserEmailInvites, loadingOrgUserEmailInvites] = useOrgUserEmailInvites()
    const [latestOffers, loadingLatestOffers] = useLatestOffers()
    const [offerDrafts, loadingOfferDrafts] = useOfferDrafts()
    const [search, setSearch] = useState(useUrlParam('s'));
    const filteredOffers = filterOffers(search, latestOffers || emptyArray)
    const filteredDrafts = filterOffers(search, offerDrafts || emptyArray)
    const [orgId] = useOrgIdLocalStorage()

    const noOffersString = "Du har ingen anbud" + (!!search ? ' som passer med søket' : '')

    const loading = loadingLatestOffers || loadingOfferDrafts || loadingOrgUser || loadingOrgUserInvites || loadingMyOrgUserInvites || loadingMyOrgUserEmailInvites || loadingOrgUserEmailInvites || loadingMyOrgUserInvites || loadingAppUser || loadingOrg

    const anonymousUserWarning = !loadingAuthUser && authUser && authUser.isAnonymous ? (
        <ListItem>
            <Alert style={{ width: '100%' }} severity="warning">NB! Du er logget inn anonymt. For å beholde arbeidet dit må du gå til <Link component={RouterLink} to={Paths.MY_APP_USER}>App Brukeren</Link> din og legge til en innloggingsmetode. Hvis du ikke ønsker å lage en bruker enda kan du gjøre dette senere</Alert>
        </ListItem>
    ) : null

    const enablePushNotificationsAlert = (appUser?.data.id && messaging.isSupported() && !(getNotificationStatus() === 'granted')) ? (
        <ListItem>
            <Alert style={{ width: '100%' }} severity="info">
                {getNotificationStatus() === 'denied' ? (
                    'Du har ikke tillatt oss å sende deg varsler! Venligst tillat varsler'
                ) : (
                    <>
                        {'Du har ikke slått på varsler! Venligst '}
                        <Button color="primary" size="small" onClick={() => {
                            registerToken(appUser.data.id).then(() => window.location.reload())
                        }}>tillat varsler</Button>
                    </>
                )}
                {' slik at vi kan oppdatere deg når det blir gjort endringer på organisasjonen din, eller når noen inviterer deg til en organisasjon'}
            </Alert>
        </ListItem>
    ) : null

    const content = (loading) ? <FullscreenLoading screenId="Offers" /> : (
        <List>
            {anonymousUserWarning}
            {enablePushNotificationsAlert}
            {orgId ? (
                <>
                    {org && !Object.keys(org.data.industryTypes.map).length ? (
                        <ListItem>
                            <Alert style={{ width: '100%' }} severity="error">Du må spesifisere en bransje for <Link component={RouterLink} to={Paths.MY_ORG}>organisasjonen din!</Link></Alert>
                        </ListItem>
                    ) : null}
                    <PriceFieldSugegstionsList appUser={appUser?.data} orgUser={orgUser?.data} />
                </>
            ) : null}
            <TrialAlert />
            <NoSubscriptionAlert />
            <MyOrgUserInvitesList appUser={appUser?.data} invites={myOrgUserInvites} emailInvites={myOrgUserEmailInvites} />
            {orgId ? (
                <>
                    <OrgUserInviteList appUser={appUser?.data} orgUser={orgUser?.data} invites={orgUserInvites} emailInvites={orgUserEmailInvites} />
                    {filteredOffers?.length ? (
                        <ListSubheader>Nyeste anbud</ListSubheader>
                    ) : null}

                    {filteredOffers?.map(offer => OfferItem({ offer: offer, }))}

                    {filteredDrafts?.length ? (
                        <ListSubheader>Utkast</ListSubheader>
                    ) : null}

                    {filteredDrafts?.map(offer => OfferItem({ offer: offer, drafts: true, }))}

                    {(filteredOffers?.length || filteredDrafts?.length) ? null : (
                        orgUser ? (
                            <ListItem component={RouterLink} to="/opprett-anbud">
                                <ListItemText primary={noOffersString} secondary="Trykk her for å komme i gang" />
                            </ListItem>
                        ) : (
                            <ListItem >
                                <ListItemText primary={noOffersString} />
                            </ListItem>

                        )
                    )}
                </>
            ) : null}
            {orgId ? null : (
                <ListItem component={RouterLink} to={Paths.SELECT_ORG}>
                    <ListItemText primary={<Link href={Paths.SELECT_ORG}>Trykk her for å velge en organisasjon</Link>} />
                </ListItem>
            )}

        </List>
    )

    return (
        <PageNavigation title={"Startsiden"} setSearch={setSearch} search={search}>
            {orgUser ? (
                <Fab
                    color="secondary"
                    aria-label="add-offer"
                    style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1050 }}
                    component={RouterLink}
                    to="/opprett-anbud"
                >
                    <Add />
                </Fab>
            ) : null
            }
            {content}
        </PageNavigation>
    )
}
